import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Correspondence, EmailCorrespondence, PhoneCorrespondence, MemoCorrespondence, Claim, generateDocumentSasUrl } from '../api/claims';
import { getSnapshot } from '../api/templates';
import { SubmissionState } from '../api/finalResponses';
import SubmissionErrorDetails from './SubmissionErrorDetails';
import PDFViewerDrawer from './PDFViewerDrawer';
import { emailSearchVariants } from '../utils/emailSearch';
import { EmailBody } from '../utils/EmailBody';
import './CorrespondenceViewer.css';

interface CorrespondenceViewerProps {
  isOpen: boolean;
  onClose: () => void;
  correspondence: Correspondence[];
  claim?: Claim;
  onPdfOpen?: (url: string, fileName: string) => void;
}

const submissionLabels: Record<SubmissionState, string> = {
  failed: 'Not sent — submission failed',
  accepted: 'Submitted to provider — delivery unconfirmed',
  unknown: 'Submission unconfirmed — do not retry',
  pending: 'Submission pending — do not retry',
  sending: 'Submission in progress — do not retry',
};

// A timestamp with an offset AND a trailing Z ("2026-11-05T11:09:18.95+00:00Z",
// written by the LOA portal and some older paths) is not a date the browser can
// read: the viewer showed "Invalid Date" and sorted it wrongly. The Z is
// redundant (the offset already says UTC), so drop it. 185,307 entries had this
// on 5 Nov 2026 (FOA re-signed, uploads, every portal LOA submission).
export const normaliseTimestamp = (value: string): string =>
  value.replace(/([+-]\d{2}:?\d{2})Z$/i, '$1');

function finalResponseSubmissionState(email: EmailCorrespondence | null): SubmissionState | null {
  if (!email || (email.source !== 'final-response-closure' &&
      !(typeof email.metadata?.finalResponseOperationId === 'string' && email.metadata.finalResponseOperationId))) return null;
  const state = email.metadata?.submissionState;
  return state === 'failed' || state === 'accepted' || state === 'pending' || state === 'sending' ? state : 'unknown';
}

// Freshdesk #604: plain-word labels for the values a template email was sent
// with (metadata.templateVariables). Unknown keys show as stored.
const TEMPLATE_VALUE_LABELS: Record<string, string> = {
  Name: 'Name',
  name: 'Name',
  claimreference: 'Claims in this email',
  claimnumbers: 'Claims in this email',
  claimcount: 'Number of claims charged',
  totalcost: 'Total fee',
  chargeableclaimreferences: 'Claims charged',
  selectedreasons: 'Outstanding requirements',
};

function sentTemplateValues(email: EmailCorrespondence | null): Array<[string, string]> {
  const values = email?.metadata?.templateVariables;
  if (!values || typeof values !== 'object' || Array.isArray(values)) return [];
  return Object.entries(values as Record<string, unknown>)
    .filter(([, value]) => typeof value === 'string' || typeof value === 'number')
    .map(([key, value]) => [TEMPLATE_VALUE_LABELS[key] ?? key, String(value)]);
}

function SentTemplateValues({ email }: { email: EmailCorrespondence }) {
  const values = sentTemplateValues(email);
  if (values.length === 0) return null;
  return (
    <div style={{ margin: '12px 0' }}>
      <p style={{ margin: '0 0 4px 0', fontWeight: 600 }}>Values in this email</p>
      <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 12px', margin: 0 }}>
        {values.map(([label, value], index) => (
          <React.Fragment key={`${label}-${index}`}>
            <dt style={{ color: '#475569' }}>{label}</dt>
            <dd style={{ margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{value}</dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
}

const CorrespondenceViewer: React.FC<CorrespondenceViewerProps> = ({
  isOpen,
  onClose,
  correspondence,
  claim,
  onPdfOpen
}) => {
  const [selectedItem, setSelectedItem] = useState<Correspondence | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'email' | 'phone' | 'memo'>('all');
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState<{ url: string; fileName: string } | null>(null);
  const [documentLoading, setDocumentLoading] = useState<string | null>(null);
  const [snapshotPreview, setSnapshotPreview] = useState<{ id: string; imagePath: string | null } | null>(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);

  // Fetch snapshot image when needed for template-based emails
  useEffect(() => {
    let cancelled = false;
    setSnapshotPreview(null);
    setSnapshotLoading(false);
    const fetchSnapshotImage = async () => {
      if (!selectedItem || selectedItem.type !== 'email') {
        return;
      }

      const emailItem = selectedItem as EmailCorrespondence;

      if (emailItem.templateImagePath) {
        return;
      }

      // Only the pinned snapshot describes this send, never today's template.
      if (emailItem.templateSnapshotId) {
        setSnapshotLoading(true);
        try {
          const snapshot = await getSnapshot(emailItem.templateSnapshotId);
          if (!cancelled) setSnapshotPreview({ id: emailItem.templateSnapshotId, imagePath: snapshot.imagePath || null });
        } catch (err) {
          console.error('Failed to fetch snapshot by ID:', err);
        } finally {
          if (!cancelled) setSnapshotLoading(false);
        }
      }
    };

    fetchSnapshotImage();
    return () => { cancelled = true; };
  }, [selectedItem]);

  // Helper to get fresh SAS URL for document access
  const openDocumentWithFreshUrl = useCallback(async (
    originalUrl: string,
    blobName: string | undefined,
    fileName: string,
    isPdf: boolean
  ) => {
    const loadingKey = blobName || originalUrl;
    setDocumentLoading(loadingKey);

    try {
      // Try to get a fresh SAS URL
      const response = await generateDocumentSasUrl({
        blobUrl: originalUrl,
        blobName: blobName
      });

      const freshUrl = response.sasUrl;

      if (isPdf) {
        if (onPdfOpen) {
          onPdfOpen(freshUrl, fileName);
        } else {
          setSelectedPdf({ url: freshUrl, fileName });
          setPdfViewerOpen(true);
        }
      } else {
        window.open(freshUrl, '_blank');
      }
    } catch (error: any) {
      console.error('Failed to get fresh document URL:', error);
      // If SAS URL generation fails, show error or try original URL as fallback
      const errorMessage = error?.response?.data?.error || 'Failed to access document';
      if (errorMessage.includes('not found')) {
        alert('Document not found in storage. It may have been deleted or moved.');
      } else {
        // Fallback to original URL (might work if it's a public blob or still has valid token)
        console.warn('Falling back to original URL');
        if (isPdf) {
          if (onPdfOpen) {
            onPdfOpen(originalUrl, fileName);
          } else {
            setSelectedPdf({ url: originalUrl, fileName });
            setPdfViewerOpen(true);
          }
        } else {
          window.open(originalUrl, '_blank');
        }
      }
    } finally {
      setDocumentLoading(null);
    }
  }, [onPdfOpen]);

  // Normalise legacy correspondence entries to prevent crashes
  const normalisedCorrespondence = useMemo(() => {
    return correspondence.map((item, index) => {
      const normalised: any = { ...item };
      // Ensure id exists
      if (!normalised.id) {
        normalised.id = `legacy_${index}_${Date.now()}`;
      }
      // Normalise legacy type values (e.g. "Email Sent" -> "email")
      if (normalised.type && normalised.type !== 'email' && normalised.type !== 'phone' && normalised.type !== 'memo') {
        if (normalised.type.toLowerCase().includes('email')) {
          normalised.type = 'email';
        }
      }
      // Normalise legacy direction values
      if ('direction' in normalised) {
        const dir = normalised.direction;
        if (dir === 'outbound' || dir === 'outgoing') {
          normalised.direction = 'sent';
        }
      }
      // Ensure timestamp exists (fall back to date field)
      if (!normalised.timestamp && (normalised as any).date) {
        normalised.timestamp = (normalised as any).date;
      }
      if (typeof normalised.timestamp === 'string') {
        normalised.timestamp = normaliseTimestamp(normalised.timestamp);
      }
      if (!normalised.timestamp) {
        normalised.timestamp = new Date(0).toISOString();
      }
      // Ensure email fields exist
      if (normalised.type === 'email') {
        if (!normalised.to) normalised.to = [];
        if (!normalised.from) normalised.from = '';
        if (!normalised.body && normalised.content) normalised.body = normalised.content;
        if (!normalised.bodyPreview && normalised.content) normalised.bodyPreview = normalised.content.substring(0, 200);
      }
      return normalised as Correspondence;
    });
  }, [correspondence]);

  // Sort correspondence by timestamp (newest first)
  const sortedCorrespondence = useMemo(() => {
    return [...normalisedCorrespondence].sort((a, b) => {
      const dateA = new Date(a.timestamp || 0).getTime();
      const dateB = new Date(b.timestamp || 0).getTime();
      return dateB - dateA;
    });
  }, [normalisedCorrespondence]);

  // Filter correspondence based on search and type
  const filteredCorrespondence = useMemo(() => {
    return sortedCorrespondence.filter(item => {
      // Type filter
      if (filterType !== 'all' && item.type !== filterType) {
        return false;
      }

      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        // gmail.com and googlemail.com are the same mailbox: match a from/to
        // address against both domain variants of the search term.
        const emailVariants = emailSearchVariants(searchTerm);
        const matchesEmailField = (value: string) =>
          emailVariants.some((v) => value.includes(v));
        const subject = ('subject' in item ? item.subject || '' : '').toLowerCase();
        const from = ('from' in item ? item.from || '' : '').toLowerCase();
        const bodyPreview = ('bodyPreview' in item ? item.bodyPreview || '' : '').toLowerCase();

        if (item.type === 'email') {
          const emailItem = item as EmailCorrespondence;
          const to = emailItem.to.join(' ').toLowerCase();
          return subject.includes(searchLower) ||
                 matchesEmailField(from) ||
                 matchesEmailField(to) ||
                 bodyPreview.includes(searchLower);
        } else if (item.type === 'phone') {
          const phoneItem = item as PhoneCorrespondence;
          const to = (phoneItem.to || '').toLowerCase();
          const notes = (phoneItem.callNotes || '').toLowerCase();
          return subject.includes(searchLower) ||
                 matchesEmailField(from) ||
                 matchesEmailField(to) ||
                 notes.includes(searchLower);
        } else if (item.type === 'memo') {
          const memoItem = item as MemoCorrespondence;
          const title = (memoItem.title || '').toLowerCase();
          return title.includes(searchLower);
        }
      }

      return true;
    });
  }, [sortedCorrespondence, searchTerm, filterType]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (days < 7) {
      return date.toLocaleDateString([], { weekday: 'short' }) + ' ' +
             date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  const renderTimelineItem = (item: Correspondence, index: number) => {
    const isEmail = item.type === 'email';
    const isPhone = item.type === 'phone';
    const isMemo = item.type === 'memo';
    const isSelected = selectedItem?.id === item.id;
    const isOutgoing = 'direction' in item && (item.direction === 'sent' || item.direction === 'outgoing');
    const isSigned = isEmail && (item as EmailCorrespondence).status === 'signed';
    const emailItem = isEmail ? item as EmailCorrespondence : null;
    const submissionState = finalResponseSubmissionState(emailItem);
    const memoItem = isMemo ? item as MemoCorrespondence : null;

    // Determine the icon
    const getIcon = () => {
      if (isSigned) return '✓';
      if (isEmail) return '📧';
      if (isPhone) return '📞';
      if (isMemo) return '📋';
      return '📄';
    };

    // Determine CSS class for dot
    const getDotClass = () => {
      if (isSigned) return 'signed';
      if (isEmail) return 'email';
      if (isPhone) return 'phone';
      if (isMemo) return 'memo';
      return '';
    };

    return (
      <div
        key={item.id}
        className={`timeline-item ${isSelected ? 'selected' : ''} ${isMemo ? 'memo' : isOutgoing ? 'outgoing' : 'incoming'} ${isSigned ? 'signed' : ''}`}
        onClick={() => setSelectedItem(item)}
      >
        {/* Timeline connector */}
        {index < filteredCorrespondence.length - 1 && (
          <div className="timeline-connector" />
        )}

        {/* Timeline dot */}
        <div className={`timeline-dot ${getDotClass()}`}>
          {getIcon()}
        </div>

        {/* Timeline content */}
        <div className="timeline-content">
          <div className="timeline-header">
            <div className="timeline-subject">
              {isMemo ? memoItem?.title : ('subject' in item ? item.subject : '') || '(No subject)'}
            </div>
            {isMemo ? (
              <div className="timeline-direction memo">
                📋 File Review
              </div>
            ) : isSigned ? (
              <div className="timeline-direction signed">
                ✓ Signed
              </div>
            ) : (
              <div className={`timeline-direction ${isOutgoing ? 'sent' : 'received'}`} style={submissionState ? { whiteSpace: 'normal', flexShrink: 1 } : undefined}>
                {submissionState ? submissionLabels[submissionState] :
                  isOutgoing ? `↗️ Sent by ${(item as any).actor || (item as any).from || ''}` : '↘️ Received'}
              </div>
            )}
          </div>

          <div className="timeline-meta">
            {!isMemo && (
              <span className="timeline-from">
                {isOutgoing ? 'To: ' : 'From: '}
                {isOutgoing ? (
                  isEmail ?
                    ((item as EmailCorrespondence).to?.[0] || '') :
                    (item as PhoneCorrespondence).to
                ) : 'from' in item ? item.from : ''}
              </span>
            )}
            {isMemo && memoItem?.autoGenerated && (
              <span className="timeline-from auto-generated">
                🤖 Auto-generated
              </span>
            )}
            <span className="timeline-timestamp">
              {formatTimestamp(item.timestamp)}
            </span>
          </div>

          {item.type === 'phone' && (
            <div className="timeline-call-info">
              <span className={`call-status ${(item as PhoneCorrespondence).callAnswered ? 'answered' : 'missed'}`}>
                {(item as PhoneCorrespondence).callAnswered ? '✓ Answered' : '✗ Missed'}
              </span>
              {(item as PhoneCorrespondence).callDuration > 0 && (
                <span className="call-duration">
                  Duration: {formatDuration((item as PhoneCorrespondence).callDuration)}
                </span>
              )}
            </div>
          )}

          {/* Show document count for memos */}
          {isMemo && memoItem?.documents && memoItem.documents.length > 0 && (
            <div className="timeline-memo-docs">
              📎 {memoItem.documents.length} document{memoItem.documents.length > 1 ? 's' : ''} attached
            </div>
          )}

          {/* Show attachment link for signed documents */}
          {isSigned && (emailItem?.attachmentUrl || (emailItem?.documents && emailItem.documents.length > 0)) && (
            <div className="timeline-attachment">
              <a
                href={emailItem.documents?.[0]?.url || emailItem.attachmentUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                  // Prioritize documents[0].url as it has the SAS token
                  const url = emailItem.documents?.[0]?.url || emailItem.attachmentUrl;
                  if (url) {
                    window.open(url, '_blank');
                  }
                }}
                className="signed-document-link"
              >
                CLICK TO VIEW SIGNED DOCUMENT
              </a>
            </div>
          )}

          {isEmail && emailItem?.templateName && (
            <div className="timeline-template-tag">
              📋 {emailItem.templateName}
            </div>
          )}

          {!isMemo && (
            <div className="timeline-preview">
              {isEmail && emailItem?.bodyPreview ? emailItem.bodyPreview :
               isPhone ? ((item as PhoneCorrespondence).callNotes?.substring(0, 100) || 'No preview available') :
               'No preview available'}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderContentViewer = () => {
    if (!selectedItem) {
      return (
        <div className="content-empty">
          <div className="empty-icon">💬</div>
          <p>Select a correspondence item to view details</p>
        </div>
      );
    }

    const isEmail = selectedItem.type === 'email';
    const isPhone = selectedItem.type === 'phone';
    const isMemo = selectedItem.type === 'memo';
    const emailItem = isEmail ? selectedItem as EmailCorrespondence : null;
    const submissionState = finalResponseSubmissionState(emailItem);
    const phoneItem = isPhone ? selectedItem as PhoneCorrespondence : null;
    const memoItem = isMemo ? selectedItem as MemoCorrespondence : null;

    return (
      <div className="content-viewer">
        <div className="content-header">
          <h3>{isMemo ? memoItem?.title : ('subject' in selectedItem ? selectedItem.subject : '') || '(No subject)'}</h3>
          {submissionState && <>
            <p role="status">{submissionLabels[submissionState]}</p>
            {emailItem?.actor && <p>Requested by: {emailItem.actor}</p>}
            {submissionState !== 'accepted' && <SubmissionErrorDetails error={emailItem?.metadata?.submissionError} />}
          </>}
          <div className="content-meta">
            <div className="meta-row">
              <strong>Type:</strong>
              <span className="type-badge">
                {isEmail ? '📧 Email' : isPhone ? '📞 Phone Call' : '📋 Memo'}
              </span>
            </div>

            {!isMemo && (
              <>
                <div className="meta-row">
                  <strong>From:</strong> {'from' in selectedItem ? selectedItem.from : ''}
                </div>

                <div className="meta-row">
                  <strong>To:</strong>
                  {isEmail ? (emailItem?.to || []).join(', ') : phoneItem?.to}
                </div>
              </>
            )}

            {isEmail && emailItem?.cc && emailItem.cc.length > 0 && (
              <div className="meta-row">
                <strong>CC:</strong> {emailItem.cc.join(', ')}
              </div>
            )}

            <div className="meta-row">
              <strong>Date:</strong> {new Date(selectedItem.timestamp).toLocaleString()}
            </div>

            {!isMemo && 'direction' in selectedItem && (
              <div className="meta-row">
                <strong>Direction:</strong>
                <span className={`direction-badge ${selectedItem.direction}`}>
                  {selectedItem.direction === 'sent' || selectedItem.direction === 'outgoing' ?
                    '↗️ Outgoing' : '↘️ Incoming'}
                </span>
              </div>
            )}

            {isPhone && phoneItem && (
              <>
                <div className="meta-row">
                  <strong>Status:</strong>
                  <span className={`call-status ${phoneItem.callAnswered ? 'answered' : 'missed'}`}>
                    {phoneItem.callAnswered ? '✓ Answered' : '✗ Missed'}
                  </span>
                </div>
                {phoneItem.callDuration > 0 && (
                  <div className="meta-row">
                    <strong>Duration:</strong> {formatDuration(phoneItem.callDuration)}
                  </div>
                )}
              </>
            )}


            {isMemo && memoItem?.source && (
              <div className="meta-row">
                <strong>Source:</strong> {memoItem.source}
              </div>
            )}

            {isEmail && emailItem?.source === 'email-reply-tracking' && (
              <div className="meta-row">
                <span className="auto-tracked-badge">🤖 Auto-tracked</span>
              </div>
            )}

            {isEmail && emailItem?.autoGenerated && (
              <div className="meta-row">
                <span className="auto-generated-badge">⚡ Auto-generated</span>
              </div>
            )}

            {isMemo && memoItem?.autoGenerated && (
              <div className="meta-row">
                <span className="auto-generated-badge">🤖 Auto-generated</span>
              </div>
            )}

            {isEmail && emailItem?.templateName && (
              <div className="meta-row">
                <strong>Template:</strong>
                <span className="template-badge">
                  📋 {emailItem.templateName}
                  {emailItem.templateSnapshotId && (
                    <a
                      href={`/admin/email-templates?snapshot=${emailItem.templateSnapshotId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="template-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      View
                    </a>
                  )}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="content-body">
          {(() => {
            // Determine the effective image path (from stored data or fetched snapshot)
            const effectiveImagePath = emailItem?.templateImagePath ||
              (snapshotPreview?.id === emailItem?.templateSnapshotId ? snapshotPreview?.imagePath : null);

            if (isEmail && emailItem) {
              const isOutgoingHtml = (emailItem?.direction as string) === 'sent' || (emailItem?.direction as string) === 'outbound';
              return (
                <div>
                  {isOutgoingHtml && emailItem?.from && (
                    <div style={{
                      padding: '10px 14px',
                      marginBottom: '12px',
                      backgroundColor: '#eff6ff',
                      borderLeft: '3px solid #2563eb',
                      borderRadius: '4px',
                      fontSize: '13px',
                      color: '#1e40af',
                      fontWeight: 500
                    }}>
                      ↗️ {emailItem.from} — {emailItem.timestamp ? new Date(emailItem.timestamp).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                    </div>
                  )}
                  {emailItem.body ? <EmailBody body={emailItem.body} /> :
                    <pre className="content-text">{emailItem.bodyPreview}</pre>}
                  {snapshotLoading && (
                <div className="template-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading template preview...</p>
                </div>
                  )}
                  {effectiveImagePath && (
                <div className="template-preview-container">
                  <div className="template-preview-header">
                    <span className="template-preview-label">Saved Template Preview</span>
                    {(emailItem?.templateSnapshotId || emailItem?.templateId) && (
                      <a
                        href={`/admin/email-templates?snapshot=${emailItem.templateSnapshotId || ''}&template=${emailItem.templateId || ''}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="template-view-full-link"
                      >
                        View Full Template
                      </a>
                    )}
                  </div>
                  <p>Template preview; personalised values may be shown separately below.</p>
                  <div className="template-preview-image-container">
                    <img
                      src={effectiveImagePath}
                      alt={`Template: ${emailItem?.templateName || 'Email Template'}`}
                      className="template-preview-image"
                    />
                  </div>
                </div>
                  )}
                  {!snapshotLoading && !effectiveImagePath && (emailItem.templateId || emailItem.templateSnapshotId) && (
                    <p>No saved template preview is available for this email.</p>
                  )}
                  <SentTemplateValues email={emailItem} />
                  {emailItem.metadata?.sentInputs != null && typeof emailItem.metadata.sentInputs === 'object' && (
                    <details>
                      <summary>Saved send inputs</summary>
                      <p>Values recorded when this email was submitted for sending, not a reconstructed copy of the delivered email.</p>
                      <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{JSON.stringify(emailItem.metadata.sentInputs, null, 2)}</pre>
                    </details>
                  )}
                </div>
              );
            } else if (isPhone && phoneItem?.callNotes) {
              return (
                <div className="phone-notes">
                  <h4>Call Notes</h4>
                  <pre className="notes-content">{phoneItem.callNotes}</pre>
                </div>
              );
            } else if (isMemo && memoItem) {
              return (
                <div className="memo-content">
                  <div className="memo-title-section">
                    <h4>📋 {memoItem.title}</h4>
                    {memoItem.autoGenerated && (
                      <span className="memo-auto-badge">Auto-generated by system</span>
                    )}
                  </div>
                  {memoItem.content && (
                    <div className="memo-text-content" style={{ whiteSpace: 'pre-wrap', padding: '12px 0', lineHeight: '1.6' }}>
                      {memoItem.content}
                    </div>
                  )}
                  {memoItem.documents && memoItem.documents.length > 0 && (
                    <div className="memo-documents">
                      <h5>Attached Documents</h5>
                      <div className="attachments-list">
                        {memoItem.documents.map((doc, index) => {
                          const fileName = doc.label || doc.blobName || `Document ${index + 1}`;
                          const isPdf = fileName.toLowerCase().endsWith('.pdf') ||
                                       doc.type?.toLowerCase().includes('pdf') ||
                                       doc.blobName?.toLowerCase().endsWith('.pdf') || false;
                          const isLoading = documentLoading === (doc.blobName || doc.url);

                          return doc.url ? (
                            <button
                              key={`memo-doc-${index}`}
                              onClick={() => openDocumentWithFreshUrl(doc.url!, doc.blobName, fileName, isPdf)}
                              disabled={isLoading}
                              className="attachment-link memo-doc-link"
                            >
                              {isLoading ? '⏳' : '📄'} {fileName}
                            </button>
                          ) : (
                            <span key={`memo-doc-${index}`} className="attachment-text">
                              📄 {fileName}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            } else {
              const isOutgoing = isEmail && ((emailItem?.direction as string) === 'sent' || (emailItem?.direction as string) === 'outbound');
              return (
                <div>
                  {isOutgoing && emailItem?.from && (
                    <div style={{
                      padding: '10px 14px',
                      marginBottom: '12px',
                      backgroundColor: '#eff6ff',
                      borderLeft: '3px solid #2563eb',
                      borderRadius: '4px',
                      fontSize: '13px',
                      color: '#1e40af',
                      fontWeight: 500
                    }}>
                      ↗️ {emailItem.from} — {emailItem.timestamp ? new Date(emailItem.timestamp).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                    </div>
                  )}
                  <pre className="content-text">{'bodyPreview' in selectedItem ? selectedItem.bodyPreview : 'No content available'}</pre>
                </div>
              );
            }
          })()}

          {/* Show documents for any correspondence type (email, memo, etc.) */}
          {((selectedItem as any)?.documents?.length > 0 || (isEmail && (emailItem?.hasAttachments || (emailItem?.attachments && emailItem.attachments.length > 0)))) && (
            <div className="attachments-section">
              <h4>📎 Attachments</h4>
              {(selectedItem as any).documents && (selectedItem as any).documents.length > 0 ? (
                <div className="attachments-list">
                  {(selectedItem as any).documents.map((doc: any, index: number) => {
                    const fileName = doc.label || doc.blobName || doc.type || `Document ${index + 1}`;
                    const isPdf = fileName.toLowerCase().endsWith('.pdf') ||
                                 doc.type?.toLowerCase().includes('pdf') ||
                                 doc.blobName?.toLowerCase().endsWith('.pdf') || false;
                    const isLoading = documentLoading === (doc.blobName || doc.url);

                    return doc.url ? (
                      <button
                        key={`doc-${index}`}
                        onClick={() => openDocumentWithFreshUrl(doc.url!, doc.blobName, fileName, isPdf)}
                        disabled={isLoading}
                        className="attachment-link"
                        style={{
                          background: 'none',
                          border: 'none',
                          color: isLoading ? '#999' : 'inherit',
                          cursor: isLoading ? 'wait' : 'pointer',
                          textDecoration: 'underline',
                          padding: 0
                        }}
                      >
                        {isLoading ? '⏳' : '📄'} {fileName}
                      </button>
                    ) : (
                      <span key={`doc-${index}`} className="attachment-text">
                        📄 {fileName}
                      </span>
                    );
                  })}
                </div>
              ) : isEmail && emailItem?.attachments && emailItem.attachments.length > 0 ? (
                <div className="attachments-list">
                  {emailItem.attachments.map((att, index) => {
                    const fileName = att.name || att.filename || `Attachment ${index + 1}`;
                    const blobName = att.blob_name;
                    const isPdf = fileName.toLowerCase().endsWith('.pdf') ||
                                 att.content_type?.toLowerCase().includes('pdf') ||
                                 blobName?.toLowerCase().endsWith('.pdf') || false;
                    const isLoading = documentLoading === (blobName || att.url);

                    // If we have a blob_name, we can generate a URL
                    if (blobName || att.url) {
                      return (
                        <button
                          key={`att-${index}`}
                          onClick={() => openDocumentWithFreshUrl(att.url || '', blobName, fileName, isPdf)}
                          disabled={isLoading}
                          className="attachment-link"
                          style={{
                            background: 'none',
                            border: 'none',
                            color: isLoading ? '#999' : 'inherit',
                            cursor: isLoading ? 'wait' : 'pointer',
                            textDecoration: 'underline',
                            padding: 0
                          }}
                        >
                          {isLoading ? '⏳' : '📄'} {fileName}
                        </button>
                      );
                    }
                    return (
                      <span key={`att-${index}`} className="attachment-text">
                        📄 {fileName}
                      </span>
                    );
                  })}
                </div>
              ) : (
                <p className="no-attachments">Attachments were sent with this email but aren't stored for viewing in the timeline.</p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="correspondence-viewer-overlay" onClick={onClose}>
      <div className="correspondence-viewer-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-left">
            <h2>Correspondence Timeline</h2>
            {claim && (
              <span className="claim-reference">
                {claim.proclaimReference || claim.id} - {claim.firstName} {claim.lastName}
              </span>
            )}
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-filters">
          <input
            type="text"
            placeholder="Search correspondence..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <div className="filter-buttons">
            <button
              className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
              onClick={() => setFilterType('all')}
            >
              All ({normalisedCorrespondence.length})
            </button>
            <button
              className={`filter-btn ${filterType === 'email' ? 'active' : ''}`}
              onClick={() => setFilterType('email')}
            >
              📧 Emails ({normalisedCorrespondence.filter(c => c.type === 'email').length})
            </button>
            <button
              className={`filter-btn ${filterType === 'phone' ? 'active' : ''}`}
              onClick={() => setFilterType('phone')}
            >
              📞 Calls ({normalisedCorrespondence.filter(c => c.type === 'phone').length})
            </button>
            <button
              className={`filter-btn ${filterType === 'memo' ? 'active' : ''}`}
              onClick={() => setFilterType('memo')}
            >
              📋 Memos ({normalisedCorrespondence.filter(c => c.type === 'memo').length})
            </button>
          </div>
        </div>

        <div className="modal-body">
          <div className="timeline-panel">
            {filteredCorrespondence.length > 0 ? (
              <div className="timeline-list">
                {filteredCorrespondence.map((item, index) => renderTimelineItem(item, index))}
              </div>
            ) : (
              <div className="no-correspondence">
                {searchTerm || filterType !== 'all' ?
                  'No correspondence matching your filters' :
                  'No correspondence yet'}
              </div>
            )}
          </div>

          <div className="content-panel">
            {renderContentViewer()}
          </div>
        </div>

        {/* PDF Viewer Drawer */}
        {selectedPdf && (
          <PDFViewerDrawer
            open={pdfViewerOpen}
            onClose={() => {
              setPdfViewerOpen(false);
              setSelectedPdf(null);
            }}
            pdfUrl={selectedPdf.url}
            fileName={selectedPdf.fileName}
          />
        )}
      </div>
    </div>
  );
};

export default CorrespondenceViewer;
